import { z } from 'zod';

export const infoUserSchema = z.object({
    id: z.number().int().positive(),
    name: z.string().min(1),
    avatar_url: z.string().nullable().optional(),
    profile_picture: z.string().nullable().optional(),
    advisor_id: z.number().int().positive().optional(),
});

export const infoRoleKeySchema = z.enum([
    'ah_chair',
    'ah_vice',
    'treasurer',
    'liaison',
    'hut_manager',
    'X',
    'XX',
    'XXX',
]);

export type InfoRoleKey = z.infer<typeof infoRoleKeySchema>;

const infoRoleReferenceSchema = z.object({
    user_id: z.number().int().positive().nullable().optional(),
    user_name: z.string().nullable().optional(),
    avatar_url: z.string().nullable().optional(),
});

const infoAdvisorApiSchema = z.object({
    id: z.number().int().positive(),
    user_id: z.number().int().positive().nullable().optional(),
    user_name: z.string().nullable().optional(),
    avatar_url: z.string().nullable().optional(),
});

const infoRoleAssignmentsApiSchema = z.object({
    ah_chair: infoRoleReferenceSchema.nullable().optional().default({ user_id: null, user_name: null, avatar_url: null }),
    ah_vice: infoRoleReferenceSchema.nullable().optional().default({ user_id: null, user_name: null, avatar_url: null }),
    treasurer: infoRoleReferenceSchema.nullable().optional().default({ user_id: null, user_name: null, avatar_url: null }),
    liaison: infoRoleReferenceSchema.nullable().optional().default({ user_id: null, user_name: null, avatar_url: null }),
    hut_manager: infoRoleReferenceSchema.nullable().optional().default({ user_id: null, user_name: null, avatar_url: null }),
    X: infoRoleReferenceSchema.nullable().optional().default({ user_id: null, user_name: null, avatar_url: null }),
    XX: infoRoleReferenceSchema.nullable().optional().default({ user_id: null, user_name: null, avatar_url: null }),
    XXX: infoRoleReferenceSchema.nullable().optional().default({ user_id: null, user_name: null, avatar_url: null }),
    advisors: z.array(infoAdvisorApiSchema).default([]),
});

export const infoBankAccountSchema = z.object({
    id: z.number().int().positive(),
    display_name: z.string().trim().min(1),
    account_holder: z.string().trim().min(1),
    iban: z.string().trim().optional().default(''),
    bic: z.string().trim().optional().default(''),
    notes: z.string().trim().optional().default(''),
});

export const infoRoleAssignmentsSchema = infoRoleAssignmentsApiSchema.transform((roles) => {
    const mapRoleRef = (roleRef: z.infer<typeof infoRoleReferenceSchema>): z.infer<typeof infoUserSchema> | null => {
        if (!roleRef.user_id) {
            return null;
        }
        return {
            id: roleRef.user_id,
            name: roleRef.user_name ?? `User ${roleRef.user_id}`,
            avatar_url: roleRef.avatar_url ?? null,
        };
    };

    return {
        ah_chair: mapRoleRef(roles.ah_chair ?? { user_id: null, user_name: null, avatar_url: null }),
        ah_vice: mapRoleRef(roles.ah_vice ?? { user_id: null, user_name: null, avatar_url: null }),
        treasurer: mapRoleRef(roles.treasurer ?? { user_id: null, user_name: null, avatar_url: null }),
        liaison: mapRoleRef(roles.liaison ?? { user_id: null, user_name: null, avatar_url: null }),
        hut_manager: mapRoleRef(roles.hut_manager ?? { user_id: null, user_name: null, avatar_url: null }),
        X: mapRoleRef(roles.X ?? { user_id: null, user_name: null, avatar_url: null }),
        XX: mapRoleRef(roles.XX ?? { user_id: null, user_name: null, avatar_url: null }),
        XXX: mapRoleRef(roles.XXX ?? { user_id: null, user_name: null, avatar_url: null }),
        advisors: roles.advisors
            .flatMap((advisor) => {
                if (!advisor.user_id) {
                    return [];
                }
                return [{
                    id: advisor.user_id,
                    advisor_id: advisor.id,
                    name: advisor.user_name ?? `User ${advisor.user_id}`,
                    avatar_url: advisor.avatar_url ?? null,
                }];
            }),
    };
});

export const EMPTY_INFO_ROLES: z.infer<typeof infoRoleAssignmentsSchema> = {
    ah_chair: null,
    ah_vice: null,
    treasurer: null,
    liaison: null,
    hut_manager: null,
    X: null,
    XX: null,
    XXX: null,
    advisors: [],
};

export const infoResponseSchema = z
    .object({
        roles: infoRoleAssignmentsSchema.optional(),
        bank_accounts: z.array(infoBankAccountSchema).default([]),
        address: z
            .object({
                address_text: z.string().default(''),
            })
            .nullable()
            .optional(),
    })
    .transform(({ address, bank_accounts, roles }) => ({
        roles: roles ?? EMPTY_INFO_ROLES,
        bank_accounts,
        address: address?.address_text ?? '',
    }));

export const EMPTY_INFO_RESPONSE: z.infer<typeof infoResponseSchema> = {
    roles: EMPTY_INFO_ROLES,
    bank_accounts: [],
    address: '',
};

export const infoUpdateRolesInputSchema = z.object({
    roles: z.object({
        ah_chair: z.number().int().positive().nullable().optional(),
        ah_vice: z.number().int().positive().nullable().optional(),
        treasurer: z.number().int().positive().nullable().optional(),
        liaison: z.number().int().positive().nullable().optional(),
        hut_manager: z.number().int().positive().nullable().optional(),
        X: z.number().int().positive().nullable().optional(),
        XX: z.number().int().positive().nullable().optional(),
        XXX: z.number().int().positive().nullable().optional(),
    }),
});

export const infoAddAdvisorInputSchema = z.object({
    user_id: z.number().int().positive(),
});

export const infoDeleteAdvisorInputSchema = z.object({
    advisor_id: z.number().int().positive(),
});

export const infoBankAccountUpsertSchema = z.object({
    display_name: z.string().trim().min(1, 'Bezeichnung ist erforderlich.'),
    account_holder: z.string().trim().min(1, 'Kontoinhaber ist erforderlich.'),
    iban: z.string().trim().optional().default(''),
    bic: z.string().trim().optional().default(''),
    notes: z.string().trim().optional().default(''),
});

export const infoCreateBankAccountInputSchema = infoBankAccountUpsertSchema;

export const infoUpdateBankAccountInputSchema = infoBankAccountUpsertSchema.extend({
    id: z.number().int().positive(),
});

export const infoDeleteBankAccountInputSchema = z.object({
    id: z.number().int().positive(),
});

export const infoUpdateAddressInputSchema = z.object({
    address_text: z.string().trim(),
});

export type InfoUser = z.infer<typeof infoUserSchema>;
export type InfoRoles = z.infer<typeof infoRoleAssignmentsSchema>;
export type InfoBankAccount = z.infer<typeof infoBankAccountSchema>;
export type InfoResponse = z.infer<typeof infoResponseSchema>;
export type InfoCreateBankAccountInput = z.infer<typeof infoCreateBankAccountInputSchema>;
export type InfoUpdateBankAccountInput = z.infer<typeof infoUpdateBankAccountInputSchema>;
